import { Box, InputAdornment, Stack, TextField, TextFieldProps } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { ComponentProps, FocusEvent, useEffect, useState } from "react";
import Result from "../../utils/result.utils.ts";
import useDealBuilderAlertsStore from "../../../stores/deal-builder-alerts.store.ts";

type NumericFormatAllProps = ComponentProps<typeof NumericFormat<TextFieldProps>>;

interface CurrencyInputProps extends NumericFormatAllProps {
  isValueValid: (value: number) => Result<undefined>,
  onValidChange: (value: number) => void,
  value: number,
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  locked: boolean | undefined
  onLockedChange: (locked: boolean) => void,
  className?: string
}

export default function CurrencyInput({ 
  value,
  isValueValid,
  onValidChange,
  startAdornment,
  endAdornment,
  locked,
  onLockedChange,
  className,
  ...props
}: CurrencyInputProps) {
  const { setSnackMessage } = useDealBuilderAlertsStore();
  const [lastSetValue, setLastSetValue] = useState<number>(value);
  const [inputValue, setInputValue] = useState<number>(value);
  const isInputValid = isValueValid(inputValue);

  useEffect(() => {
    if (value !== inputValue) {
      setLastSetValue(value);
      setInputValue(value);
    }
  }, [value]);

  function updateOrReset() {
    if (isInputValid.valid) {
      setLastSetValue(inputValue);
      onValidChange(inputValue);
    } else {
      setInputValue(lastSetValue)
      setSnackMessage(isInputValid.error);
    }
  }
  
  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    event.target.select();
  }

  return (
    <NumericFormat
      {...props}
      thousandSeparator=","
      value={inputValue}
      onFocus={handleFocus}
      customInput={TextField}
      onValueChange={(val) => {
        if (val.floatValue !== undefined) {
          setInputValue(val.floatValue);
        }
      }}
      onBlur={() => {
        if (inputValue === value / 1000) {
          setInputValue(value);
        }
      }}
      slotProps={{
        ...props.slotProps,
        htmlInput: {
          ...props?.slotProps?.htmlInput,
          className: className,
          onBlur: (_e) => {
            updateOrReset();
          }
        },
        input: {
          ...props?.slotProps?.input,
          startAdornment: (
            <Stack direction="row" gap={0} alignItems="center">
              {startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
              {locked !== undefined && <GreenBubble onClick={onLockedChange} toggled={locked} />}
            </Stack>
          ),
          endAdornment: (
            <Stack direction="row" gap={0} alignItems="center">
              {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
            </Stack>
          ),
          style: { textAlign: 'right' }
        }
      }}
      sx={{
        ...(!isInputValid.valid && {
          '& fieldset': {
            ...props.sx?.["& fieldset"],
            border: '1px solid #ff8585ff !important',
          },
        }),
        '& .MuiInputBase-input': {
          ...props.sx?.["& .MuiInputBase-input"],
          textAlign: 'right',
        },
        ...props.sx
      }}
    />
  );
}

interface GreenBubbleProps {
  toggled: boolean,
  onClick: (toggled: boolean) => void
}

function GreenBubble({ toggled, onClick }: GreenBubbleProps) {
  return (
    <Box sx={{
      cursor: "pointer",
      width: 12,
      height: 11,
      mt: "2px",
      borderRadius: "50%",
      ...(toggled
        ? {
            backgroundColor: "green",
            border: "1px solid transparent",
          }
        : {
            border: "1px dashed gray",
          }),
      }}
      onClick={() => onClick(!toggled)}
    />
  );
  
}
